import React from 'react';
import { useQuery } from 'react-query';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/Layout/PageHeader';
import { statusPillSx, surfaceCardSx, tableHeadCellSx, tokens } from '../../theme/tokens';

function statusKind(status) {
  if (status === 'Active') return 'active';
  if (status === 'Pending') return 'pending';
  if (status === 'Suspended') return 'danger';
  return 'inactive';
}

function initials(member) {
  const a = (member.first_name || '').trim().slice(0, 1);
  const b = (member.last_name || '').trim().slice(0, 1);
  return (`${a}${b}` || 'M').toUpperCase();
}

function avatarTone(seed) {
  const tones = ['#1E5C46', '#8A6415', '#4A5D8A', '#7C4A8A', '#356A6B', '#8A4A5D'];
  const n = String(seed || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return tones[n % tones.length];
}

function formatJoinedYear(member) {
  const raw = member.created_at || member.date_joined || member.joined_at;
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '—' : String(d.getFullYear());
}

function formatUpdated(member) {
  const raw = member.updated_at || member.created_at;
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startToday - startThat) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatCloseLine(election) {
  if (!election?.end_date) return null;
  const end = new Date(election.end_date);
  if (Number.isNaN(end.getTime())) return null;
  const time = (election.end_time || '17:00').toString().slice(0, 5);
  const [hh, mm] = time.split(':');
  const h = Number(hh);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  const day = end.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return `Closes ${day}, ${h12}:${mm || '00'} ${suffix}`;
}

function StatCard({ label, value, delta, deltaUp, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...surfaceCardSx,
        p: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .15s',
        '&:hover': onClick ? { borderColor: tokens.primary } : {},
      }}
    >
      <Box sx={{ px: 2.5, py: 2.25 }}>
        <Typography
          sx={{
            fontSize: 12,
            color: tokens.muted,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.fontDisplay,
            fontSize: 28,
            fontWeight: 700,
            mt: 0.75,
            color: tokens.ink,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.15,
          }}
        >
          {value}
        </Typography>
        {delta && (
          <Typography
            sx={{
              fontSize: 12,
              mt: 0.5,
              color: deltaUp ? tokens.primary : tokens.muted,
              fontWeight: deltaUp ? 600 : 400,
            }}
          >
            {delta}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function CardTitle({ children, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 2.5,
        py: 2,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 15, fontWeight: 600, color: tokens.ink }}>
        {children}
      </Typography>
      {action}
    </Box>
  );
}

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { staff, refreshStaff } = useAuth();
  const permissionError = searchParams.get('error');

  const hasPermission = (perm) => {
    const perms = staff?.permissions || [];
    return perms.includes('all') || perms.includes(perm);
  };

  const { data, isLoading, error } = useQuery(
    'dashboardOverview',
    async () => {
      const [metricsRes, activeRes, pendingRes, recentRes, electionsRes] = await Promise.all([
        api.get('/dashboard/metrics'),
        hasPermission('members')
          ? api.get('/members', { params: { status: 'Active', page: 1, limit: 1 } }).catch(() => null)
          : Promise.resolve(null),
        hasPermission('members')
          ? api.get('/members', { params: { status: 'Pending', page: 1, limit: 1 } }).catch(() => null)
          : Promise.resolve(null),
        hasPermission('members')
          ? api.get('/members', { params: { status: 'all', page: 1, limit: 4 } }).catch(() => null)
          : Promise.resolve(null),
        hasPermission('hr')
          ? api.get('/hr/elections', { params: { status: 'active', page: 1, limit: 1 } }).catch(() => null)
          : Promise.resolve(null),
      ]);

      const metrics = metricsRes?.data?.data || {};
      const totalMembers = Number(metrics.members) || 0;
      const activeCount = Number(activeRes?.data?.pagination?.total) || 0;
      const pendingCount = Number(pendingRes?.data?.pagination?.total) || 0;
      const recentMembers = recentRes?.data?.data || [];
      const liveElection = (electionsRes?.data?.data || []).find((e) => e.status === 'active')
        || (electionsRes?.data?.data || [])[0]
        || null;

      const votes = Number(liveElection?.total_votes) || 0;
      const turnoutPct = totalMembers > 0 && liveElection
        ? Math.min(100, Math.round((votes / totalMembers) * 1000) / 10)
        : null;

      return {
        metrics,
        totalMembers,
        activeCount,
        pendingCount,
        recentMembers,
        liveElection,
        votes,
        turnoutPct,
        activeShare: totalMembers > 0 ? Math.round((activeCount / totalMembers) * 1000) / 10 : 0,
      };
    },
    {
      retry: 1,
      refetchOnWindowFocus: true,
    }
  );

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = staff?.first_name || 'there';

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={36} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={100} height={18} sx={{ mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5 }}>
                <Skeleton width="40%" />
                <Skeleton width="55%" height={36} />
                <Skeleton width="50%" />
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2 }}>
              <Skeleton height={220} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton height={120} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton height={140} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    const isConnectionRefused = error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK';
    const message = isConnectionRefused
      ? 'Cannot reach the server. Make sure the backend is running (e.g. npm start or node server/index.js in the project root on port 5001).'
      : (error?.response?.data?.message || error?.message || 'Unknown error');
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}
      >
        <Typography component="span" fontWeight={600}>Error loading dashboard.</Typography> {message}
      </Alert>
    );
  }

  const {
    totalMembers = 0,
    activeCount = 0,
    pendingCount = 0,
    recentMembers = [],
    liveElection = null,
    votes = 0,
    turnoutPct = null,
    activeShare = 0,
    metrics = {},
  } = data || {};

  const closeLine = formatCloseLine(liveElection);

  return (
    <Box>
      <PageHeader
        crumbs="Overview"
        title={`${greeting}, ${firstName}`}
        actions={
          hasPermission('members') ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/dashboard/members/new')}
              sx={{ bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primaryStrong } }}
            >
              Add member
            </Button>
          ) : null
        }
      />

      {permissionError && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: '9px' }}
          onClose={() => setSearchParams({})}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={async () => {
                await refreshStaff();
                setSearchParams({});
              }}
            >
              Refresh my permissions
            </Button>
          }
        >
          <Box>
            {decodeURIComponent(permissionError)}
            <Typography component="span" variant="body2" sx={{ display: 'block', mt: 1 }}>
              If an admin just granted you access, click &quot;Refresh my permissions&quot; or log out and log back in.
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Stat row — matches prototype */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total members"
            value={totalMembers.toLocaleString('en-GH')}
            delta={metrics.staffs != null ? `${Number(metrics.staffs).toLocaleString('en-GH')} staff accounts` : null}
            onClick={hasPermission('members') ? () => navigate('/dashboard/members') : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Active"
            value={activeCount.toLocaleString('en-GH')}
            delta={`${activeShare}% of registry`}
            onClick={hasPermission('members') ? () => navigate('/dashboard/members') : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Pending review"
            value={pendingCount.toLocaleString('en-GH')}
            delta="Awaiting verification"
            onClick={hasPermission('members') ? () => navigate('/dashboard/members?status=Pending') : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Election turnout"
            value={turnoutPct != null ? `${turnoutPct}%` : '—'}
            delta={
              liveElection
                ? `▲ Live — ${liveElection.name}`
                : 'No active election'
            }
            deltaUp={!!liveElection}
            onClick={hasPermission('hr') ? () => navigate('/dashboard/hr/elections') : undefined}
          />
        </Grid>
      </Grid>

      {/* Two-column: recent members + side panels */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden' }}>
            <CardTitle
              action={
                <Typography
                  component={RouterLink}
                  to="/dashboard/members"
                  sx={{ fontSize: 13, fontWeight: 500, color: tokens.primary, textDecoration: 'none' }}
                >
                  View all
                </Typography>
              }
            >
              Recently updated members
            </CardTitle>
            {recentMembers.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center', color: tokens.muted }}>
                <Typography sx={{ fontWeight: 600, color: tokens.ink, mb: 0.5 }}>No members yet</Typography>
                <Typography variant="body2">Add members to see them here.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Member</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, display: { xs: 'none', sm: 'table-cell' } }}>Region</TableCell>
                      <TableCell sx={tableHeadCellSx}>Status</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, display: { xs: 'none', md: 'table-cell' } }}>Joined</TableCell>
                      <TableCell sx={{ ...tableHeadCellSx, display: { xs: 'none', sm: 'table-cell' } }}>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentMembers.map((member) => (
                      <TableRow
                        key={member.id}
                        hover
                        onClick={() => navigate(`/dashboard/members/details/${member.id}`)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#F4F7F5' },
                          '& td': { borderColor: tokens.border, py: 1.5 },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.35 }}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                bgcolor: avatarTone(member.member_id || member.id),
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {initials(member)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: tokens.ink, lineHeight: 1.25 }}>
                                {member.first_name} {member.last_name}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                                {member.member_id || '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 13.5 }}>
                          {member.region || member.constituency || '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={statusPillSx(statusKind(member.status))}>
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                            {member.status || '—'}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            display: { xs: 'none', md: 'table-cell' },
                            fontSize: 13.5,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatJoinedYear(member)}
                        </TableCell>
                        <TableCell
                          sx={{
                            display: { xs: 'none', sm: 'table-cell' },
                            fontSize: 13.5,
                          }}
                        >
                          {formatUpdated(member)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden' }}>
              <CardTitle
                action={
                  <Typography
                    component={RouterLink}
                    to="/dashboard/hr/elections"
                    sx={{ fontSize: 13, fontWeight: 500, color: tokens.primary, textDecoration: 'none' }}
                  >
                    Open
                  </Typography>
                }
              >
                {liveElection?.name || 'Elections'}
              </CardTitle>
              <Box sx={{ px: 2.5, pt: 1.75, pb: 2.5 }}>
                {liveElection ? (
                  <>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.9,
                        bgcolor: 'rgba(217,164,65,.15)',
                        color: tokens.accent,
                        px: 1.5,
                        py: 0.6,
                        borderRadius: 99,
                        fontWeight: 600,
                        fontSize: 12.5,
                      }}
                    >
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.accent }} />
                      Voting in progress
                    </Box>
                    <Typography sx={{ color: tokens.muted, fontSize: 13, mt: 1.5, lineHeight: 1.5 }}>
                      {closeLine ? `${closeLine}. ` : ''}
                      <Box component="b" sx={{ color: tokens.ink, fontVariantNumeric: 'tabular-nums' }}>
                        {votes.toLocaleString('en-GH')}
                      </Box>
                      {' '}of{' '}
                      <Box component="b" sx={{ color: tokens.ink, fontVariantNumeric: 'tabular-nums' }}>
                        {totalMembers.toLocaleString('en-GH')}
                      </Box>
                      {' '}eligible members have voted.
                    </Typography>
                    <Box sx={{ mt: 1.5, height: 9, bgcolor: '#EDF1EE', borderRadius: 99, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${Math.min(100, turnoutPct || 0)}%`,
                          height: '100%',
                          bgcolor: tokens.primary,
                          borderRadius: 99,
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Typography sx={{ color: tokens.muted, fontSize: 13.5 }}>
                    No election is live right now. Open Elections to create or review past results.
                  </Typography>
                )}
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden' }}>
              <CardTitle>Needs attention</CardTitle>
              <Box sx={{ px: 2.5, py: 2, display: 'grid', gap: 1.25, fontSize: 13.5 }}>
                <Box>
                  ⚠{' '}
                  <Box component="b" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {pendingCount.toLocaleString('en-GH')}
                  </Box>{' '}
                  registrations pending verification
                </Box>
                <Box>
                  ⚠{' '}
                  <Box component="b" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {Number(metrics.events || 0).toLocaleString('en-GH')}
                  </Box>{' '}
                  events on record
                </Box>
                <Box>
                  ⚠{' '}
                  <Box component="b" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {Number(metrics.staffs || 0).toLocaleString('en-GH')}
                  </Box>{' '}
                  staff accounts in the system
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
